import { cloneElement, isValidElement, ReactElement } from "react";
import { resolveProperty } from "@animech/axiom-utility";
import {
	FieldComponentBuilder,
	FieldComponentBuilderArgs,
	FieldPipe,
	FieldPipeMeta,
	FieldProps,
	getDefaultFieldComponentRenderers,
	renderField,
	UiComponentBuildContext,
	UiComponentBuilder,
} from "@animech/axiom-ui-builder";
import {
	CustomProductList,
	CustomProductListProps,
} from "../components/widgets/custom-product-list/custom-product-list";
import { Root } from "@definitions";
import { CustomProductListNode } from "src/definitions/ui-definitions";

type ComponentBuilderArgs = FieldComponentBuilderArgs<
	Root,
	CustomProductListNode
> & {
	fieldComponentBuilder?: FieldComponentBuilder<Root>;
	onProductSelect?: (product: unknown) => void;
	onProductDrag?: (product: unknown, dragEvent: Interact.DragEvent) => void;
};

export class CustomProductListComponentBuilder
	implements UiComponentBuilder<Root, CustomProductListNode>
{
	private _args: ComponentBuilderArgs;

	constructor(args: ComponentBuilderArgs) {
		this._args = { ...args };
	}

	public build(
		node: CustomProductListNode,
		context: UiComponentBuildContext<Root>,
	) {
		const { getFieldNodeMeta, getFieldNodeValueMeta } = this._args;
		const component = node.componentNode;
		if ("state" in component && component.state) {
			const property = resolveProperty(component.state, context.activeProperty);
			return (
				<FieldPipe key={`${node.id}`} property={property} write>
					<FieldPipeMeta
						getFieldMeta={(property) => getFieldNodeMeta(node, property)}
						getFieldValueMeta={getFieldNodeValueMeta}
					>
						{this._render(node, context)}
					</FieldPipeMeta>
				</FieldPipe>
			);
		} else {
			return this._render(node, context);
		}
	}

	private _render(
		node: CustomProductListNode,
		context: UiComponentBuildContext<Root>,
	): ReactElement<CustomProductListProps> | undefined {
		const { fieldComponentBuilder, onProductDrag, onProductSelect } =
			this._args;
		const { componentNode } = node;
		const isDraggable =
			node.draggable === true && componentNode.type === "compactCardSelect";

		const inputElement = fieldComponentBuilder
			? fieldComponentBuilder.renderField(
					node.componentNode,
					context.activeProperty,
				)
			: renderField<Root>(
					getDefaultFieldComponentRenderers(),
					node.componentNode,
				);

		const props: CustomProductListProps = {
			draggable: isDraggable,
			onProductSelect,
			onProductDrag: isDraggable ? onProductDrag : undefined,
			renderField: (props) =>
				isValidElement<FieldProps<Root>>(inputElement)
					? cloneElement<FieldProps<Root>>(inputElement, props)
					: undefined,
		};

		return <CustomProductList {...props} />;
	}
}
